import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as cartApi from '../api/cart';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], item_count: 0, subtotal_cents: 0, shipping_cents: 0, total_cents: 0 });
  const [loading, setLoading] = useState(true);

  const fetchCart = useCallback(async () => {
    try {
      const res = await cartApi.getCart();
      setCart(res.data);
    } catch {
      // cart unavailable
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCart();
  }, [fetchCart, user]);

  // Listen for cartUpdated events from other components
  useEffect(() => {
    const handler = () => fetchCart();
    window.addEventListener('cartUpdated', handler);
    return () => window.removeEventListener('cartUpdated', handler);
  }, [fetchCart]);

  const addItem = useCallback(async (data) => {
    const res = await cartApi.addItem(data);
    setCart(res.data);
    window.dispatchEvent(new Event('cartUpdated'));
    return res.data;
  }, []);

  const updateItem = useCallback(async (itemId, data) => {
    const res = await cartApi.updateItem(itemId, data);
    setCart(res.data);
    return res.data;
  }, []);

  const removeItem = useCallback(async (itemId) => {
    const res = await cartApi.removeItem(itemId);
    setCart(res.data);
    return res.data;
  }, []);

  const clearCart = useCallback(async () => {
    const res = await cartApi.clearCart();
    setCart(res.data);
    return res.data;
  }, []);

  const mergeCart = useCallback(async () => {
    await cartApi.mergeCart();
    await fetchCart();
  }, [fetchCart]);

  return (
    <CartContext.Provider value={{ cart, loading, addItem, updateItem, removeItem, clearCart, mergeCart, fetchCart }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
